import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import classes from './pricing.module.css';
import Header from '../../components/standartComponents/header';
import axios from 'axios';
import { getCSRFToken } from '../../lib/csrf';

const Pricing = ({ authUser }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [bill, setBill] = useState('m');
  // NOTE: Yearly billing toggle is hidden until backend supports annual plans
  // const [bill, setBill] = useState('m');
  const [bzModal, setBzModal] = useState(false);
  const [bzOk, setBzOk] = useState(false);
  
  const [plans, setPlans] = useState([]);
  const [isProcessing, setIsProcessing] = useState(false);

  useEffect(() => {
    const fetchPlans = async () => {
      try {
        const response = await axios.get('/api/billing/plans/', {
          withCredentials: true,
        });
        setPlans(response.data);
      } catch (error) {
        console.error('Error fetching plans:', error);
      }
    };
    fetchPlans();
  }, []);

  const handlePayment = async (productId) => {
    // If not authenticated, redirect to sign in
    if (!authUser?.isAuthenticated) {
      navigate('/signin');
      return;
    }

    setIsProcessing(true);
    try {
      const pid = productId || (premiumPlan ? premiumPlan.id : 1);
      
      const csrfToken = getCSRFToken();
      console.log('Creating subscription with product_id:', pid);
      
      const response = await axios.post('/api/billing/subscription/create/', {
        product_id: parseInt(pid)
      }, {
        withCredentials: true,
        headers: {
          'Content-Type': 'application/json',
          ...(csrfToken && { 'X-CSRFToken': csrfToken })
        }
      });
      
      console.log('Subscription created:', response.data);
      
      if (response.data.page_url) {
        console.log('Redirecting to Monobank:', response.data.page_url);
        window.location.href = response.data.page_url;
      } else {
        console.error('No page_url in response');
        setIsProcessing(false);
      }
    } catch (error) {
      console.error('Error creating subscription:', error);
      setIsProcessing(false);
      alert(t('Something went wrong. Please try again.'));
    }
  };

  const [faqOpen, setFaqOpen] = useState(null);

  const toggleFaq = (idx) => {
    setFaqOpen(faqOpen === idx ? null : idx);
  };

  const openBiz = () => {
    setBzOk(false);
    setBzModal(true);
  };

  // Check if it's a renewal (Design 13) or first purchase (Design 12)
  const isRenewal = authUser?.subscriptionType === 'premium' || authUser?.hasHadSubscription;

  const premiumPlan = plans.find(p => p.name.includes('Преміум') || p.name.includes('Premium'));
  const monthlyPrice = premiumPlan ? parseFloat(premiumPlan.price) : 299;
  // NOTE: Yearly pricing hidden until backend supports annual plans
  // const yearlyPrice = premiumPlan ? (monthlyPrice * 10) : 2988;
  // const yearlyPricePerMonth = premiumPlan ? Math.round(yearlyPrice / 12) : 249;
  // const saveAmount = (monthlyPrice * 12) - yearlyPrice;

  return (
    <>
      <Header authUser={authUser} isPricing={true} />
      <div className={classes.wrap}>
        {/* HERO */}
        <div className={classes.hero}>
          <div className={classes.badge}><span className={classes.badgeDot}>✦</span>{isRenewal ? t('pricing.renewalBadge', 'Продовжіть навчання') : t('pricing.newBadge', 'Плани, що ростуть разом з вами')}</div>
          <h1>{isRenewal ? t('pricing.renewalTitle', 'Поновіть вашу підписку') : t('pricing.newTitle', 'Оберіть план для вашого навчання')}</h1>
          <p>{isRenewal ? t('pricing.renewalDesc', 'Залишайтеся з нами та продовжуйте досягати нових вершин у вивченні англійської.') : t('pricing.newDesc', 'Почніть безкоштовно, відкрийте повний доступ до уроків або підключіть AI Lingua для команди.')}</p>
          {/* Billing toggle hidden — backend only supports monthly plans for now
          <div className={classes.toggleRow}>
            <div className={classes.toggle}>
              <button className={bill === 'm' ? classes.on : ''} onClick={() => setBill('m')}>Щомісячно</button>
              <button className={bill === 'y' ? classes.on : ''} onClick={() => setBill('y')}>Щорічно</button>
            </div>
            <span className={`${classes.saveTag} ${bill === 'y' ? classes.show : ''}`}>−17% вигідніше</span>
          </div>
          */}
        </div>

        {/* PRICING CARDS */}
        <div className={classes.cards}>
          
          {/* FREE */}
          <div className={classes.plan}>
            <div className={classes.planName}>Безкоштовно</div>
            <div className={classes.planSub}>Щоб спробувати платформу</div>
            <div className={classes.planPrice}>0 <span className={classes.sm}>грн</span></div>
            <div className={classes.planBill}>безкоштовно назавжди</div>
            <ul className={classes.feats}>
              <li><svg className={classes.fy} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>2-хвилинний пробний урок</li>
              <li><svg className={classes.fy} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>Обмежений доступ до відеоуроків</li>
              <li><svg className={classes.fy} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>Додавання слів у словник</li>
              <li><svg className={classes.fy} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>Базове збереження прогресу</li>
              <li className={classes.featOff}><svg className={classes.fn} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>AI speaking — обмежено</li>
              <li className={classes.featOff}><svg className={classes.fn} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>Персональний план — недоступний</li>
              <li className={classes.featOff}><svg className={classes.fn} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M18 6 6 18"/><path d="m6 6 12 12"/></svg>Повний каталог — недоступний</li>
            </ul>
            <button className={`${classes.pcta} ${classes.pctaG}`} onClick={() => navigate(authUser?.isAuthenticated ? '/lesson/774' : '/signup')}>Почати безкоштовно</button>
          </div>

          {/* PREMIUM */}
          <div className={`${classes.plan} ${classes.prem}`}>
            <span className={classes.pbadge}>★ Найпопулярніший</span>
            <div className={classes.planName}>{premiumPlan ? premiumPlan.name : 'Premium'}</div>
            <div className={classes.planSub}>Для індивідуального навчання</div>
            <div className={classes.planPrice}>
              {monthlyPrice} <span className={classes.sm}>грн / місяць</span>
            </div>
            <div className={classes.planBill}>
              оплата щомісячно
            </div>
            <ul className={classes.feats}>
              <li><svg className={classes.fy} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>Персональний план навчання</li>
              <li><svg className={classes.fy} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>Усі відеоуроки</li>
              <li><svg className={classes.fy} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>Video → Words → Quiz → Speaking</li>
              <li><svg className={classes.fy} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>Практика діалогів з AI</li>
              <li><svg className={classes.fy} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>Особистий словник</li>
              <li><svg className={classes.fy} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>Збереження прогресу</li>
              <li><svg className={classes.fy} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>Повторення пройдених уроків</li>
              <li><svg className={classes.fy} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>Самостійне навчання без плану</li>
              <li><svg className={classes.fy} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>Обрані відео та статистика</li>
            </ul>
            <button className={`${classes.pcta} ${classes.pctaP}`} onClick={() => handlePayment()} disabled={isProcessing}>
              {isProcessing ? 'Обробка...' : (isRenewal ? t('pricing.renewBtn', 'Поновити підписку →') : t('pricing.buyBtn', 'Оформити підписку →'))}
            </button>
          </div>

          {/* BUSINESS */}
          <div className={classes.plan}>
            <div className={classes.planName}>Business</div>
            <div className={classes.planSub}>Для компаній, шкіл та університетів</div>
            <div className={classes.planPrice} style={{fontSize: '22px'}}>Індивідуальна ціна</div>
            <div className={classes.planBill}>під вашу команду</div>
            <ul className={classes.feats}>
              <li><svg className={classes.fy} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>Усі можливості Premium</li>
              <li><svg className={classes.fy} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>Доступ для групи користувачів</li>
              <li><svg className={classes.fy} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>Прогрес кожного учня</li>
              <li><svg className={classes.fy} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>Звіти для адміністратора</li>
              <li><svg className={classes.fy} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>Навчання під цілі компанії</li>
              <li><svg className={classes.fy} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>Підтримка менеджера</li>
              <li><svg className={classes.fy} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg>Знижки за обсяг</li>
            </ul>
            <button className={`${classes.pcta} ${classes.pctaO}`} onClick={openBiz}>Зв'язатися з нами →</button>
          </div>

        </div>

        {/* COMPARISON TABLE */}
        <div className={classes.cmp}>
          <h2>Порівняння планів</h2>
          <table>
            <thead><tr><th>Функція</th><th>Free</th><th className={classes.hl}>Premium</th><th>Business</th></tr></thead>
            <tbody>
              <tr><td>Пробний урок</td><td className={classes.ck}>✓</td><td className={classes.ck}>✓</td><td className={classes.ck}>✓</td></tr>
              <tr><td>Усі відеоуроки</td><td className={classes.cp}>обмежено</td><td className={classes.ck}>✓</td><td className={classes.ck}>✓</td></tr>
              <tr><td>Персональний план</td><td className={classes.cx}>—</td><td className={classes.ck}>✓</td><td className={classes.ck}>✓</td></tr>
              <tr><td>AI speaking</td><td className={classes.cp}>обмежено</td><td className={classes.ck}>✓</td><td className={classes.ck}>✓</td></tr>
              <tr><td>Особистий словник</td><td className={classes.ck}>✓</td><td className={classes.ck}>✓</td><td className={classes.ck}>✓</td></tr>
              <tr><td>Прогрес навчання</td><td className={classes.cp}>базовий</td><td className={classes.ck}>✓</td><td className={classes.ck}>✓</td></tr>
              <tr><td>Груповий доступ</td><td className={classes.cx}>—</td><td className={classes.cx}>—</td><td className={classes.ck}>✓</td></tr>
              <tr><td>Адмін-звіти</td><td className={classes.cx}>—</td><td className={classes.cx}>—</td><td className={classes.ck}>✓</td></tr>
              <tr><td>Менеджер підтримки</td><td className={classes.cx}>—</td><td className={classes.cx}>—</td><td className={classes.ck}>✓</td></tr>
            </tbody>
          </table>
        </div>

        {/* FAQ */}
        <div className={classes.faq}>
          <h2>Часті запитання</h2>
          <div className={classes.faqList}>
            <div className={`${classes.faqItem} ${faqOpen === 0 ? classes.open : ''}`}>
              <div className={classes.fq} onClick={() => toggleFaq(0)}>Чи можна почати безкоштовно?<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg></div>
              <div className={classes.fa}><div className={classes.faInner}>Так, ви можете спробувати платформу через безкоштовний доступ і пробний урок. Реєстрація не вимагає оплати.</div></div>
            </div>
            <div className={`${classes.faqItem} ${faqOpen === 1 ? classes.open : ''}`}>
              <div className={classes.fq} onClick={() => toggleFaq(1)}>Що входить у Premium?<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg></div>
              <div className={classes.fa}><div className={classes.faInner}>Premium відкриває персональний план, усі відеоуроки, AI speaking, словник, збереження прогресу та детальну статистику навчання.</div></div>
            </div>
            <div className={`${classes.faqItem} ${faqOpen === 2 ? classes.open : ''}`}>
              <div className={classes.fq} onClick={() => toggleFaq(2)}>Чи можна скасувати підписку?<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg></div>
              <div className={classes.fa}><div className={classes.faInner}>Так, підписку можна скасувати в будь-який момент в особистому кабінеті. Доступ збережеться до кінця оплаченого періоду.</div></div>
            </div>
            <div className={`${classes.faqItem} ${faqOpen === 3 ? classes.open : ''}`}>
              <div className={classes.fq} onClick={() => toggleFaq(3)}>Що таке Business?<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg></div>
              <div className={classes.fa}><div className={classes.faInner}>Business — це індивідуальні умови для компаній, шкіл та університетів з груповим доступом, звітами та підтримкою менеджера.</div></div>
            </div>
            <div className={`${classes.faqItem} ${faqOpen === 4 ? classes.open : ''}`}>
              <div className={classes.fq} onClick={() => toggleFaq(4)}>Чи збережеться прогрес після зміни тарифу?<svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="m6 9 6 6 6-6"/></svg></div>
              <div className={classes.fa}><div className={classes.faInner}>Так, ваш прогрес зберігається у вашому акаунті незалежно від тарифного плану.</div></div>
            </div>
          </div>
        </div>

      </div>

      {/* FOOTER */}
      <div className={classes.foot}>
        <div className={classes.footIn}>
          <div className={classes.footC}>© 2026 AI Lingua. Всі права захищені.</div>
          <div className={classes.footL}>
            <a href="tel:+380682507477">+380682507477</a>
            <Link to="/termsOfUse">Умови використання</Link>
            <Link to="/privacy">Політика конфіденційності</Link>
            <Link to="/cancelAndRefund">Повернення коштів</Link>
          </div>
        </div>
      </div>



      {/* BUSINESS MODAL */}
      <div className={`${classes.ov} ${bzModal ? classes.show : ''}`} onClick={() => setBzModal(false)}>
        <div className={classes.mod} onClick={e => e.stopPropagation()}>
          {!bzOk ? (
            <div>
              <div className={classes.modT}>Отримати пропозицію для команди</div>
              <div className={classes.modS}>Залиште контактні дані, і ми зв'яжемося з вами.</div>
              <div className={classes.fr}><div className={classes.fg}><label className={classes.fl}>Ім'я</label><input className={classes.fi} placeholder="Ваше ім'я" /></div><div className={classes.fg}><label className={classes.fl}>Email</label><input className={classes.fi} type="email" placeholder="email@company.com" /></div></div>
              <div className={classes.fr}><div className={classes.fg}><label className={classes.fl}>Компанія</label><input className={classes.fi} placeholder="Назва" /></div><div className={classes.fg}><label className={classes.fl}>Кількість користувачів</label><input className={classes.fi} type="number" placeholder="10" /></div></div>
              <div className={classes.fg}><label className={classes.fl}>Повідомлення</label><textarea className={classes.fi} placeholder="Розкажіть про потреби (необов'язково)" style={{resize: 'vertical', minHeight: '60px'}}></textarea></div>
              <div className={classes.mbtn}>
                <button className={classes.mc} onClick={() => setBzModal(false)}>Скасувати</button>
                <button className={classes.ms} onClick={() => setBzOk(true)}>Надіслати заявку</button>
              </div>
            </div>
          ) : (
            <div>
              <div className={classes.suc}>
                <div className={classes.sucIc}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><path d="M20 6 9 17l-5-5"/></svg></div>
                <div className={classes.sucT}>Дякуємо!</div>
                <div className={classes.sucS}>Ми зв'яжемося з вами найближчим часом.</div>
              </div>
              <div style={{textAlign: 'center'}}><button className={classes.ms} onClick={() => setBzModal(false)} style={{width: 'auto', padding: '12px 32px'}}>Закрити</button></div>
            </div>
          )}
        </div>
      </div>
    </>
  );
};

export default Pricing;